/**
 * Centralized SVG icon path definitions.
 *
 * Each entry stores only the raw SVG geometry (paths, circles, etc.) and the
 * viewBox. The `SvgIcon` component renders these as normal React SVG elements.
 *
 * ## Adding a new icon
 *
 * 1. Find the icon in @mui/icons-material (or any SVG source).
 * 2. Extract the `viewBox` and `<path d="...">` data.
 * 3. Add an entry to `iconDefinitions` below.
 *
 * Example: open `node_modules/@mui/icons-material/YourIcon.js` and copy the
 * `d` attribute value from each `<path>` element.
 *
 * ## Tree-shaking note
 *
 * All icons live in one object — this means the entire definitions file is
 * included in the bundle even if only one icon is used. For ~80 icons with
 * path-only data this is ~10–15 KB uncompressed (well under 5 KB gzipped),
 * which is far smaller than `@mui/icons-material`'s per-icon overhead.
 *
 * If the icon set grows to 500+ icons and bundle size becomes a concern:
 *   - Option A: Split into domain-grouped files (e.g. `navigationIcons.ts`,
 *     `actionIcons.ts`) and use barrel re-exports.
 *   - Option B: One file per icon, re-exported from this barrel.
 *
 * For 50–150 icons the single-file approach is recommended for simplicity.
 */

// ─── Types ────────────────────────────────────────────────────────────────────

export type SvgPathDefinition = {
  d: string;
  opacity?: number | string;
  fill?: string;
  fillRule?: "nonzero" | "evenodd" | "inherit";
  clipRule?: "nonzero" | "evenodd" | "inherit";
};

export type SvgCircleDefinition = {
  cx: string | number;
  cy: string | number;
  r: string | number;
  opacity?: number | string;
  fill?: string;
};

export type SvgRectDefinition = {
  x: string | number;
  y: string | number;
  width: string | number;
  height: string | number;
  rx?: string | number;
  opacity?: number | string;
  fill?: string;
};

export type IconDefinition = {
  viewBox: string;
  paths: SvgPathDefinition[];
  circles?: SvgCircleDefinition[];
  rects?: SvgRectDefinition[];
};

// ─── Definitions ──────────────────────────────────────────────────────────────

export const iconDefinitions = {
  add: {
    viewBox: "0 0 24 24",
    paths: [{ d: "M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6z" }],
  },
  addBox: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M19 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2m-2 10h-4v4h-2v-4H7v-2h4V7h2v4h4z",
      },
    ],
  },
  addCircle: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2m5 11h-4v4h-2v-4H7v-2h4V7h2v4h4z",
      },
    ],
  },
  addComment: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M21.99 4c0-1.1-.89-2-1.99-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h14l4 4zM17 11h-4v4h-2v-4H7V9h4V5h2v4h4z",
      },
    ],
  },
  accessTime: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2M12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8",
      },
      { d: "M12.5 7H11v6l5.25 3.15.75-1.23-4.5-2.67z" },
    ],
  },
  accountCircle: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2m0 4c1.93 0 3.5 1.57 3.5 3.5S13.93 13 12 13s-3.5-1.57-3.5-3.5S10.07 6 12 6m0 14c-2.03 0-4.43-.82-6.14-2.88C7.55 15.8 9.68 15 12 15s4.45.8 6.14 2.12C16.43 19.18 14.03 20 12 20",
      },
    ],
  },
  accessibility: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M12 2c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2m9 7h-6v13h-2v-6h-2v6H9V9H3V7h18z",
      },
    ],
  },
  airlinesRounded: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M19.59 4h-5.01c-.99 0-1.91.49-2.47 1.3L2 20h17l2.56-13.63C21.79 5.14 20.84 4 19.59 4M14.5 14c-1.38 0-2.5-1.12-2.5-2.5S13.12 9 14.5 9s2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5",
      },
    ],
  },
  airplanemodeInactive: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M10.5 7.67V3.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5V9l8.5 5v2l-4.49-1.32zm9.28 14.94 1.41-1.41-7.69-7.7-3.94-3.94-6.75-6.75-1.42 1.41 6.38 6.38L2 14v2l8.5-2.5V19L8 20.5V22l4-1 4 1v-1.5L13.5 19v-2.67z",
      },
    ],
  },
  alarmOn: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "m22 5.72-4.6-3.86-1.29 1.53 4.6 3.86zM7.88 3.39 6.6 1.86 2 5.71l1.29 1.53zM12 4c-4.97 0-9 4.03-9 9s4.02 9 9 9c4.97 0 9-4.03 9-9s-4.03-9-9-9m0 16c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7m-1.46-5.47L8.41 12.4l-1.06 1.06 3.18 3.18 6-6-1.06-1.06z",
      },
    ],
  },
  allInclusive: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M18.6 6.62c-1.44 0-2.8.56-3.77 1.53L12 10.66 10.48 12h.01L7.8 14.39c-.64.64-1.49.99-2.4.99-1.87 0-3.39-1.51-3.39-3.38S3.53 8.62 5.4 8.62c.91 0 1.76.35 2.44 1.03l1.13 1 1.51-1.34L9.22 8.2C8.2 7.18 6.84 6.62 5.4 6.62 2.42 6.62 0 9.04 0 12s2.42 5.38 5.4 5.38c1.44 0 2.8-.56 3.77-1.53l2.83-2.5.01.01L13.52 12h-.01l2.69-2.39c.64-.64 1.49-.99 2.4-.99 1.87 0 3.39 1.51 3.39 3.38s-1.52 3.38-3.39 3.38c-.9 0-1.76-.35-2.44-1.03l-1.14-1.01-1.51 1.34 1.27 1.12c1.02 1.01 2.37 1.57 3.82 1.57 2.98 0 5.4-2.41 5.4-5.38s-2.42-5.37-5.4-5.37",
      },
    ],
  },
  announcement: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2m-7 9h-2V5h2zm0 4h-2v-2h2z",
      },
    ],
  },
  arrowDownward: {
    viewBox: "0 0 24 24",
    paths: [{ d: "m20 12-1.41-1.41L13 16.17V4h-2v12.17l-5.58-5.59L4 12l8 8z" }],
  },
  arrowDropDown: {
    viewBox: "0 0 24 24",
    paths: [{ d: "m7 10 5 5 5-5z" }],
  },
  arrowUpward: {
    viewBox: "0 0 24 24",
    paths: [{ d: "m4 12 1.41 1.41L11 7.83V20h2V7.83l5.58 5.59L20 12l-8-8z" }],
  },
  attachment: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M2 12.5C2 9.46 4.46 7 7.5 7H18c2.21 0 4 1.79 4 4s-1.79 4-4 4H9.5C8.12 15 7 13.88 7 12.5S8.12 10 9.5 10H17v2H9.41c-.55 0-.55 1 0 1H18c1.1 0 2-.9 2-2s-.9-2-2-2H7.5C5.57 9 4 10.57 4 12.5S5.57 16 7.5 16H17v2H7.5C4.46 18 2 15.54 2 12.5",
      },
    ],
  },
  backspace: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M22 3H7c-.69 0-1.23.35-1.59.88L0 12l5.41 8.11c.36.53.9.89 1.59.89h15c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2m-3 12.59L17.59 17 14 13.41 10.41 17 9 15.59 12.59 12 9 8.41 10.41 7 14 10.59 17.59 7 19 8.41 15.41 12z",
      },
    ],
  },
  build: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "m22.7 19-9.1-9.1c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4",
      },
    ],
  },
  chat: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2M6 9h12v2H6zm8 5H6v-2h8zm4-6H6V6h12z",
      },
    ],
  },
  check: {
    viewBox: "0 0 24 24",
    paths: [{ d: "M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" }],
  },
  checkBox: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M19 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.11 0 2-.9 2-2V5c0-1.1-.89-2-2-2m-9 14-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8z",
      },
    ],
  },
  checkBoxOutlineBlank: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M19 5v14H5V5zm0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2",
      },
    ],
  },
  checkCircle: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2m-2 15-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8z",
      },
    ],
  },
  checkCircleOutline: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M16.59 7.58 10 14.17l-3.59-3.58L5 12l5 5 8-8zM12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2m0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8",
      },
    ],
  },
  checkCircleRounded: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2M9.29 16.29 5.7 12.7a.996.996 0 0 1 0-1.41c.39-.39 1.02-.39 1.41 0L10 14.17l6.88-6.88c.39-.39 1.02-.39 1.41 0s.39 1.02 0 1.41l-7.59 7.59c-.38.39-1.02.39-1.41 0",
      },
    ],
  },
  checklistRtl: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M11 7H2v2h9zm0 8H2v2h9zm5.34-4L12.8 7.46l1.41-1.41 2.12 2.12 4.24-4.24L22 5.34zm0 8-3.54-3.54 1.41-1.41 2.12 2.12 4.24-4.24L22 13.34z",
      },
    ],
  },
  clear: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z",
      },
    ],
  },
  close: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z",
      },
    ],
  },
  closeOutlined: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z",
      },
    ],
  },
  cloudDownload: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96M17 13l-5 5-5-5h3V9h4v4z",
      },
    ],
  },
  comment: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M21.99 4c0-1.1-.89-2-1.99-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h14l4 4zM18 14H6v-2h12zm0-3H6V9h12zm0-3H6V6h12z",
      },
    ],
  },
  delete: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6zM19 4h-3.5l-1-1h-5l-1 1H5v2h14z",
      },
    ],
  },
  description: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8zm2 16H8v-2h8zm0-4H8v-2h8zm-3-5V3.5L18.5 9z",
      },
    ],
  },
  directionsWalk: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M13.5 5.5c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2M9.8 8.9 7 23h2.1l1.8-8 2.1 2v6h2v-7.5l-2.1-2 .6-3C14.8 12 16.8 13 19 13v-2c-1.9 0-3.5-1-4.3-2.4l-1-1.6c-.4-.6-1-1-1.7-1-.3 0-.5.1-.8.1L6 8.3V13h2V9.6z",
      },
    ],
  },
  download: {
    viewBox: "0 0 24 24",
    paths: [{ d: "M5 20h14v-2H5zM19 9h-4V3H9v6H5l7 7z" }],
  },
  edit: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M3 17.25V21h3.75L17.81 9.94l-3.75-3.75zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34a.996.996 0 0 0-1.41 0l-1.83 1.83 3.75 3.75z",
      },
    ],
  },
  errorOutline: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M11 15h2v2h-2zm0-8h2v6h-2zm.99-5C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2M12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8",
      },
    ],
  },
  flightTakeoff: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M2.5 19h19v2h-19zm19.57-9.36c-.21-.8-1.04-1.28-1.84-1.06L14.92 10l-6.9-6.43-1.93.51 4.14 7.17-4.97 1.33-1.97-1.54-1.45.39 2.59 4.49s7.12-1.9 16.57-4.43c.81-.23 1.28-1.05 1.07-1.85",
      },
    ],
  },
  folderDelete: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M22 8v10c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2l.01-12c0-1.1.89-2 1.99-2h6l2 2h8c1.1 0 2 .9 2 2m-5.5 2V9h-2v1H12v1.5h1v4c0 .83.67 1.5 1.5 1.5h2c.83 0 1.5-.67 1.5-1.5v-4h1V10zm0 5.5h-2v-4h2z",
      },
    ],
  },
  formatListNumbered: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M2 17h2v.5H3v1h1v.5H2v1h3v-4H2zm1-9h1V4H2v1h1zm-1 3h1.8L2 13.1v.9h3v-1H3.2L5 10.9V10H2zm5-6v2h14V5zm0 14h14v-2H7zm0-6h14v-2H7z",
      },
    ],
  },
  fullscreen: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M7 14H5v5h5v-2H7zm-2-4h2V7h3V5H5zm12 7h-3v2h5v-5h-2zM14 5v2h3v3h2V5z",
      },
    ],
  },
  fullscreenExit: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M5 16h3v3h2v-5H5zm3-8H5v2h5V5H8zm6 11h2v-3h3v-2h-5zm2-11V5h-2v5h5V8z",
      },
    ],
  },
  groups: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M12 12.75c1.63 0 3.07.39 4.24.9 1.08.48 1.76 1.56 1.76 2.73V18H6v-1.61c0-1.18.68-2.26 1.76-2.73 1.17-.52 2.61-.91 4.24-.91M4 13c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2m1.13 1.1c-.37-.06-.74-.1-1.13-.1-.99 0-1.93.21-2.78.58C.48 14.9 0 15.62 0 16.43V18h4.5v-1.61c0-.83.23-1.61.63-2.29M20 13c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2m4 3.43c0-.81-.48-1.53-1.22-1.85-.85-.37-1.79-.58-2.78-.58-.39 0-.76.04-1.13.1.4.68.63 1.46.63 2.29V18H24zM12 6c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3",
      },
    ],
  },
  highlightOffRounded: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M13.89 8.7 12 10.59 10.11 8.7a.996.996 0 0 0-1.41 0c-.39.39-.39 1.02 0 1.41L10.59 12 8.7 13.89c-.39.39-.39 1.02 0 1.41s1.02.39 1.41 0L12 13.41l1.89 1.89c.39.39 1.02.39 1.41 0s.39-1.02 0-1.41L13.41 12l1.89-1.89c.39-.39.39-1.02 0-1.41-.39-.38-1.03-.38-1.41 0M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2m0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8",
      },
    ],
  },
  history: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M13 3c-4.97 0-9 4.03-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42C8.27 19.99 10.51 21 13 21c4.97 0 9-4.03 9-9s-4.03-9-9-9m-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8z",
      },
    ],
  },
  hourglassFull: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M6 2v6h.01L6 8.01 10 12l-4 4 .01.01H6V22h12v-5.99h-.01L18 16l-4-4 4-3.99-.01-.01H18V2z",
      },
    ],
  },
  indeterminateCheckBox: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2m-2 10H7v-2h10z",
      },
    ],
  },
  info: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2m1 15h-2v-6h2zm0-8h-2V7h2z",
      },
    ],
  },
  infoOutlined: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M11 7h2v2h-2zm0 4h2v6h-2zm1-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2m0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8",
      },
    ],
  },
  keyboardArrowLeft: {
    viewBox: "0 0 24 24",
    paths: [{ d: "M15.41 16.59 10.83 12l4.58-4.59L14 6l-6 6 6 6z" }],
  },
  keyboardArrowRight: {
    viewBox: "0 0 24 24",
    paths: [{ d: "M8.59 16.59 13.17 12 8.59 7.41 10 6l6 6-6 6z" }],
  },
  keyboardDoubleArrowDown: {
    viewBox: "0 0 24 24",
    paths: [
      { d: "M18 6.41 16.59 5 12 9.58 7.41 5 6 6.41l6 6z" },
      { d: "m18 13-1.41-1.41L12 16.17l-4.59-4.58L6 13l6 6z" },
    ],
  },
  keyboardDoubleArrowUp: {
    viewBox: "0 0 24 24",
    paths: [
      { d: "M6 17.59 7.41 19 12 14.42 16.59 19 18 17.59l-6-6z" },
      { d: "m6 11 1.41 1.41L12 7.83l4.59 4.58L18 11l-6-6z" },
    ],
  },
  link: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1M8 13h8v-2H8zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5",
      },
    ],
  },
  localAirport: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M22 16v-2l-8.5-5V3.5c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5V9L2 14v2l8.5-2.5V19L8 20.5V22l4-1 4 1v-1.5L13.5 19v-5.5z",
      },
    ],
  },
  lock: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2m-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2m3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1s3.1 1.39 3.1 3.1z",
      },
    ],
  },
  menu: {
    viewBox: "0 0 24 24",
    paths: [{ d: "M3 18h18v-2H3zm0-5h18v-2H3zm0-7v2h18V6z" }],
  },
  menuBook: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M21 5c-1.11-.35-2.33-.5-3.5-.5-1.95 0-4.05.4-5.5 1.5-1.45-1.1-3.55-1.5-5.5-1.5S2.45 4.9 1 6v14.65c0 .25.25.5.5.5.1 0 .15-.05.25-.05C3.1 20.45 5.05 20 6.5 20c1.95 0 4.05.4 5.5 1.5 1.35-.85 3.8-1.5 5.5-1.5 1.65 0 3.35.3 4.75 1.05.1.05.15.05.25.05.25 0 .5-.25.5-.5V6c-.6-.45-1.25-.75-2-1m0 13.5c-1.1-.35-2.3-.5-3.5-.5-1.7 0-4.15.65-5.5 1.5V8c1.35-.85 3.8-1.5 5.5-1.5 1.2 0 2.4.15 3.5.5z",
      },
    ],
  },
  newReleases: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "m23 12-2.44-2.78.34-3.68-3.61-.82-1.89-3.18L12 3 8.6 1.54 6.71 4.72l-3.61.81.34 3.68L1 12l2.44 2.78-.34 3.69 3.61.82 1.89 3.18L12 21l3.4 1.46 1.89-3.18 3.61-.82-.34-3.68zm-10 5h-2v-2h2zm0-4h-2V7h2z",
      },
    ],
  },
  nightlightRound: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M12.01 12c0-3.57 2.2-6.62 5.31-7.87.89-.36.75-1.69-.19-1.9-1.1-.24-2.27-.3-3.48-.14-4.51.6-8.12 4.31-8.59 8.83C4.44 16.93 9.13 22 15.01 22c.73 0 1.43-.08 2.12-.23.95-.21 1.1-1.53.2-1.9-3.22-1.29-5.33-4.41-5.32-7.87",
      },
    ],
  },
  notInterested: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2m0 18c-4.42 0-8-3.58-8-8 0-1.85.63-3.55 1.69-4.9L16.9 18.31C15.55 19.37 13.85 20 12 20m6.31-3.1L7.1 5.69C8.45 4.63 10.15 4 12 4c4.42 0 8 3.58 8 8 0 1.85-.63 3.55-1.69 4.9",
      },
    ],
  },
  notifications: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2m6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1z",
      },
    ],
  },
  notificationsActive: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M7.58 4.08 6.15 2.65C3.75 4.48 2.17 7.3 2.03 10.5h2c.15-2.65 1.51-4.97 3.55-6.42m12.39 6.42h2c-.15-3.2-1.73-6.02-4.12-7.85l-1.42 1.43c2.02 1.45 3.39 3.77 3.54 6.42M18 11c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2zm-6 11c.14 0 .27-.01.4-.04.65-.14 1.18-.58 1.44-1.18q.15-.36.15-.78h-4c.01 1.1.9 2 2.01 2",
      },
    ],
  },
  person: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4m0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4",
      },
    ],
  },
  phoneAndroidOutlined: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M16 1H8C6.34 1 5 2.34 5 4v16c0 1.66 1.34 3 3 3h8c1.66 0 3-1.34 3-3V4c0-1.66-1.34-3-3-3m1 17H7V4h10zm-3 3h-4v-1h4z",
      },
    ],
  },
  print: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M19 8H5c-1.66 0-3 1.34-3 3v6h4v4h12v-4h4v-6c0-1.66-1.34-3-3-3m-3 11H8v-5h8zm3-7c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1m-1-9H6v4h12z",
      },
    ],
  },
  priorityHigh: {
    viewBox: "0 0 24 24",
    paths: [{ d: "M10 3h4v12h-4z" }],
    circles: [{ cx: 12, cy: 19, r: 2 }],
  },
  public: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2m-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39",
      },
    ],
  },
  removeCircle: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2m5 11H7v-2h10z",
      },
    ],
  },
  reportProblemOutlined: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M12 5.99 19.53 19H4.47zM12 2 1 21h22zm1 14h-2v2h2zm0-6h-2v4h2z",
      },
    ],
  },
  save: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M17 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V7zm-5 16c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3m3-10H5V5h10z",
      },
    ],
  },
  scheduleOutlined: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2M12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8m.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z",
      },
    ],
  },
  school: {
    viewBox: "0 0 24 24",
    paths: [
      { d: "M5 13.18v4L12 21l7-3.82v-4L12 17zM12 3 1 9l11 6 9-4.91V17h2V9z" },
    ],
  },
  search: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14",
      },
    ],
  },
  send: {
    viewBox: "0 0 24 24",
    paths: [{ d: "M2.01 21 23 12 2.01 3 2 10l15 2-15 2z" }],
  },
  shoppingCart: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2M1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1H5.21l-.94-2zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2",
      },
    ],
  },
  soap: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "m9.12 5-7.18 6.79c-.6.56-.94 1.35-.94 2.18V20c0 1.66 1.34 3 3 3h13.75c.69 0 1.25-.56 1.25-1.25s-.56-1.25-1.25-1.25H12v-1h7.75c.69 0 1.25-.56 1.25-1.25S20.44 17 19.75 17H12v-1h8.75c.69 0 1.25-.56 1.25-1.25s-.56-1.25-1.25-1.25H12v-1h6.75c.69 0 1.25-.56 1.25-1.25S19.44 10 18.75 10H8.86c.64-1.11 1.48-2.58 1.49-2.61.09-.16.14-.33.14-.53 0-.26-.09-.5-.26-.7C10.22 6.12 9.12 5 9.12 5M14 6.25c.41 0 .75.34.75.75s-.34.75-.75.75-.75-.34-.75-.75.34-.75.75-.75m0-1.5c-1.24 0-2.25 1.01-2.25 2.25S12.76 9.25 14 9.25 16.25 8.24 16.25 7 15.24 4.75 14 4.75m5.75.75c.28 0 .5.22.5.5s-.22.5-.5.5-.5-.22-.5-.5.22-.5.5-.5m0-1.5c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2M16.5 1c-.83 0-1.5.67-1.5 1.5S15.67 4 16.5 4 18 3.33 18 2.5 17.33 1 16.5 1",
      },
    ],
  },
  stop: {
    viewBox: "0 0 24 24",
    paths: [{ d: "M6 6h12v12H6z" }],
  },
  stopCircle: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2m4 14H8V8h8z",
      },
    ],
  },
  title: {
    viewBox: "0 0 24 24",
    paths: [{ d: "M5 4v3h5.5v12h3V7H19V4z" }],
  },
  upload: {
    viewBox: "0 0 24 24",
    paths: [{ d: "M5 20h14v-2H5zm0-10h4v6h6v-6h4l-7-7z" }],
  },
  visibility: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5M12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5m0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3",
      },
    ],
  },
  work: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M20 6h-4V4c0-1.11-.89-2-2-2h-4c-1.11 0-2 .89-2 2v2H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2m-6 0h-4V4h4z",
      },
    ],
  },
  attachMoney: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4",
      },
    ],
  },
  settings: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6",
      },
    ],
  },
  lightMode: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M12 7c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5M2 13h2c.55 0 1-.45 1-1s-.45-1-1-1H2c-.55 0-1 .45-1 1s.45 1 1 1m18 0h2c.55 0 1-.45 1-1s-.45-1-1-1h-2c-.55 0-1 .45-1 1s.45 1 1 1M11 2v2c0 .55.45 1 1 1s1-.45 1-1V2c0-.55-.45-1-1-1s-1 .45-1 1m0 18v2c0 .55.45 1 1 1s1-.45 1-1v-2c0-.55-.45-1-1-1s-1 .45-1 1M5.99 4.58c-.39-.39-1.03-.39-1.41 0-.39.39-.39 1.03 0 1.41l1.06 1.06c.39.39 1.03.39 1.41 0s.39-1.03 0-1.41zm12.37 12.37c-.39-.39-1.03-.39-1.41 0-.39.39-.39 1.03 0 1.41l1.06 1.06c.39.39 1.03.39 1.41 0 .39-.39.39-1.03 0-1.41zm1.06-10.96c.39-.39.39-1.03 0-1.41-.39-.39-1.03-.39-1.41 0l-1.06 1.06c-.39.39-.39 1.03 0 1.41s1.03.39 1.41 0zM7.05 18.36c.39-.39.39-1.03 0-1.41-.39-.39-1.03-.39-1.41 0l-1.06 1.06c-.39.39-.39 1.03 0 1.41s1.03.39 1.41 0z",
      },
    ],
  },
  palette: {
    viewBox: "0 0 24 24",
    paths: [
      {
        d: "M12 22C6.49 22 2 17.51 2 12S6.49 2 12 2s10 4.04 10 9c0 3.31-2.69 6-6 6h-1.77c-.28 0-.5.22-.5.5 0 .12.05.23.13.33.41.47.64 1.06.64 1.67 0 1.38-1.12 2.5-2.5 2.5m0-18c-4.41 0-8 3.59-8 8s3.59 8 8 8c.28 0 .5-.22.5-.5a.54.54 0 0 0-.14-.35c-.41-.46-.63-1.05-.63-1.65 0-1.38 1.12-2.5 2.5-2.5H16c2.21 0 4-1.79 4-4 0-3.86-3.59-7-8-7",
      },
    ],
    circles: [
      { cx: 6.5, cy: 11.5, r: 1.5 },
      { cx: 9.5, cy: 7.5, r: 1.5 },
      { cx: 14.5, cy: 7.5, r: 1.5 },
      { cx: 17.5, cy: 11.5, r: 1.5 },
    ],
  },
  filterList: {
    viewBox: "0 0 24 24",
    paths: [{ d: "M10 18h4v-2h-4zM3 6v2h18V6zm3 7h12v-2H6z" }],
  },
  expandMore: {
    viewBox: "0 0 24 24",
    paths: [{ d: "M16.59 8.59 12 13.17 7.41 8.59 6 10l6 6 6-6z" }],
  },
} as const;

// ─── Derived type ─────────────────────────────────────────────────────────────

/** Union of all valid icon names. Produces a TS error if an invalid name is used. */
export type IconName = keyof typeof iconDefinitions;
